// VenuesListingPage (fixed)
// Replace your existing file content with this component

import PartyPackageCard from './PartyPackageCard';
import PartyPackageCardSkeleton from './PartyPackageCardSkeleton';
import './PartyPackageListingPage.css';
import './VenuesListingPage.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import FilterBar from './FilterBar';
import FilterModal from './FilterModel';
import { Helmet } from 'react-helmet-async';
import { API_BASE_URL } from '../utils';
import { useGeoLocation } from '../context/LocationContext';
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import VenuesFilterCarousel from './comp/VenuesFilterCarousel';
import VenuesCardNew from './comp/VenuesCardNew';
import BidNewCardSkeleton from './comp/BidNewCardSkeleton';
import HeaderTitlewithBack from '../TopTitle/HeaderTitlewithBack';

const filters = [
    {
        label: "Distance",
        type: "dropdown",
        icon: "IoIosArrowDown",
        options: ["Under 5 Km", "Under 10 Km", "Under 20 Km", "Under 30 Km", "Under 50 Km"]
    },
    {
        label: "Review",
        type: "dropdown",
        icon: "IoIosArrowDown",
        options: ["1 stars", "2 stars", "3 stars", "4 stars", "5 stars"]
    }
];

export default function VenuesListingPage() {
    const { latitude, longitude } = useGeoLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { category, slug, city } = useParams();
    const { pathname } = useLocation();
    const loaderRef = useRef(null);

    // Single source of truth for filters
    const [filtersAndSort, setFiltersAndSort] = useState({
        selectedDropdowns: {},
        selectedPills: [],
    });
    const [showFilter, setShowFilter] = useState(false);

    // Fetch venue types
    const fetchVenueTypes = async () => {
        const res = await axios.get(`${API_BASE_URL}/master/APIs/Common/getVenueTypes`);
        if (res.data.status) return res.data.data;
        throw new Error("Failed to fetch venue types");
    };

    const { data: venueType = [] } = useQuery({
        queryKey: ['venueTypes'],
        queryFn: fetchVenueTypes,
        staleTime: 1000 * 60 * 60 * 5,
        cacheTime: 1000 * 60 * 60 * 10,
        refetchOnWindowFocus: false,
    });

    // Handle slug/city/category parsing
    let finalCity = city?.replace(/-/g, "-") || "";
    let finalCategory = category?.replace(/-/g, " ") || "";

    if (slug) {
        const slugParams = slug.replace(/-/g, " ");
        const isCategoryParams = venueType.find(
            (item) => item?.name && item.name.toLowerCase().trim() === slugParams.toLowerCase().trim()
        );
        if (isCategoryParams) finalCategory = slugParams;
        else finalCity = slug;
    }

    const pageName = (finalCity || pathname.split("/").pop() || "").replace(/-/g, " ");

    // Fetch clubs (infinite query)
    const fetchClubs = async ({ pageParam = 1 }) => {
        const formData = new FormData();
        formData.append("latitude", latitude);
        formData.append("longitude", longitude);
        formData.append("city", finalCity);
        formData.append("category", finalCategory);
        formData.append("keyword", "");
        formData.append("limit", 10);
        formData.append("page", pageParam);

        const url = finalCity || finalCategory
            ? `${API_BASE_URL}/master/APIs/Common/allClubByCategoryAndCity`
            : `${API_BASE_URL}/master/APIs/Common/allClubNew`;

        const res = await axios.post(url, formData);
        const { data, status } = res.data;

        if (!status) return { data: [], nextPage: null };

        return {
            data: data || [],
            nextPage: data.length === 10 ? pageParam + 1 : null
        };
    };

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isError,
        error
    } = useInfiniteQuery({
        queryKey: ['clubs', { latitude, longitude, finalCity, finalCategory }],
        queryFn: fetchClubs,
        getNextPageParam: (lastPage) => lastPage.nextPage,
        enabled: !!latitude && !!longitude,
        staleTime: 1000 * 60 * 60 * 5,
        cacheTime: 1000 * 60 * 60 * 24,
        refetchOnWindowFocus: false,
    });

    const mergedData = data?.pages.flatMap((page) => page.data) || [];

    // Infinite scroll observer
    useEffect(() => {
        if (!loaderRef.current) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            { root: null, rootMargin: "20px", threshold: 0.1 }
        );
        observer.observe(loaderRef.current);
        return () => observer.disconnect();
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    // -------------------------
    // Load filters from URL once (mount)
    // -------------------------
    useEffect(() => {
        const review = searchParams.get("review");
        const distance = searchParams.get("distance");
        const pillsParam = searchParams.get("filter");
        const pills = pillsParam ? pillsParam.split(",") : [];

        const hasFilters = Boolean(review || distance || (pills && pills.length > 0));
        if (hasFilters) {
            setFiltersAndSort({
                selectedDropdowns: {
                    ...(review && { Review: [review] }),
                    ...(distance && { Distance: distance }),
                },
                selectedPills: pills,
            });
        }
        // run only once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // -------------------------
    // Sync filters -> URL safely (preserve other params like `source`)
    // -------------------------
    useEffect(() => {
        // read current search from location (always up-to-date)
        const currentParams = new URLSearchParams(window.location.search);

        const { selectedDropdowns = {}, selectedPills = [] } = filtersAndSort;

        // Review
        if (selectedDropdowns?.Review?.length > 0) {
            currentParams.set('review', selectedDropdowns.Review[0]);
        } else {
            currentParams.delete('review');
        }

        // Distance
        if (selectedDropdowns?.Distance) {
            const dist = Array.isArray(selectedDropdowns.Distance) ? selectedDropdowns.Distance[0] : selectedDropdowns.Distance;
            currentParams.set('distance', dist);
        } else {
            currentParams.delete('distance');
        }

        // Pills (filter)
        if (selectedPills?.length > 0) {
            currentParams.set('filter', selectedPills.join(','));
        } else {
            currentParams.delete('filter');
        }

        // Keep other existing params (e.g., source) automatically because we mutated currentParams
        const newSearch = currentParams.toString();
        const curSearch = window.location.search.replace('?', '');

        if (newSearch !== curSearch) {
            // only update when something changed to avoid loops
            setSearchParams(currentParams, { replace: true });
        }
        // Depend on a stable stringified version to avoid infinite loops caused by
        // object identity changes elsewhere
    }, [JSON.stringify(filtersAndSort), setSearchParams]);

    // -------------------------
    // Compute filter-wise counts (useMemo)
    // -------------------------
    const filterWiseCount = useMemo(() => {
        const counts = {};
        const clubs = mergedData || [];

        const distanceOptions = ["Under 5 Km", "Under 10 Km", "Under 20 Km", "Under 30 Km", "Under 50 Km"];
        distanceOptions.forEach((opt) => {
            const distanceValue = parseInt(opt.match(/\d+/)?.[0] || "0", 10);
            counts[opt] = clubs.filter((c) => {
                const d = parseFloat(c.distance);
                return !isNaN(d) && d <= distanceValue;
            }).length;
        });

        const reviewOptions = ["1 stars", "2 stars", "3 stars", "4 stars", "5 stars"];
        reviewOptions.forEach((opt) => {
            const selectedReview = parseInt(opt);
            counts[opt] = clubs.filter((c) => {
                const rating = parseFloat(c.totalAverageRating);
                return !isNaN(rating) && rating >= selectedReview - 0.4 && rating < selectedReview + 0.5;
            }).length;
        });

        return counts;
    }, [mergedData]);

    // -------------------------
    // Compute filtered results (useMemo) — no setState here
    // -------------------------
    const filtered = useMemo(() => {
        const { selectedDropdowns = {}, selectedPills = [] } = filtersAndSort;
        let isFilterApplied = false;

        const res = mergedData.filter((item) => {
            // Review
            if (selectedDropdowns?.Review?.length > 0) {
                isFilterApplied = true;
                const selectedReview = parseInt(selectedDropdowns.Review[0]);
                const rating = parseFloat(item.totalAverageRating);
                if (isNaN(rating) || rating < selectedReview - 0.4 || rating >= selectedReview + 0.5) {
                    return false;
                }
            }

            // Distance
            if (selectedDropdowns?.Distance) {
                isFilterApplied = true;
                const raw = Array.isArray(selectedDropdowns.Distance) ? selectedDropdowns.Distance[0] : selectedDropdowns.Distance;
                const selectedDistance = parseInt(raw.match(/\d+/)?.[0] || "0", 10);
                const itemDistance = parseFloat(item.distance);
                if (isNaN(itemDistance) || itemDistance > selectedDistance) return false;
            }

            return true;
        });

        return isFilterApplied ? res : [];
    }, [mergedData, JSON.stringify(filtersAndSort)]);

    // Display dataset: filtered if any filter applied, otherwise mergedData
    const displayData = filtered.length > 0 ? filtered : mergedData;

    return (
        <>
            <Helmet>
                <title>{pageName ? `${pageName} - ` : ""}Instant Table Booking – Deals on Clubs & Restaurants Nearby</title>
                <meta name="description" content={`Reserve tables online at top party venues${pageName ? ` in ${pageName}` : " in Delhi-NCR"}. Get amazing offers on clubs, cafes, and restaurants with PartyWitty.`} />
            </Helmet>

            <div className="party-package-section">
                <HeaderTitlewithBack title={"Venues"} />

                <div className="party-package-list">
                    <div className="party-package-page-filter-container">
                        <FilterBar
                            filterList={filters}
                            filtersAndSort={filtersAndSort}
                            setFiltersAndSort={setFiltersAndSort}
                            filterWiseCount={filterWiseCount}
                            setShowFilter={setShowFilter}
                        />
                    </div>

                    {filtered.length > 0 && (
                        <div className='bid-listing-page-new-header-crousale'>
                            <VenuesFilterCarousel data={filtered} />
                        </div>
                    )}

                    <div className="party-package-page-container">
                        {isLoading ? (
                            Array.from({ length: 6 }).map((_, i) => <BidNewCardSkeleton key={i} />)
                        ) : (
                            displayData.map((item, i) => (
                                <VenuesCardNew key={item.id || i} data={item} isListingPage={true} isVenueList={true} />
                            ))
                        )}

                        {isFetchingNextPage &&
                            Array.from({ length: 6 }).map((_, i) => <BidNewCardSkeleton key={`loading-${i}`} />)}

                        <div ref={loaderRef} style={{ height: "1px" }} />
                        {!hasNextPage && mergedData.length > 0 && <p className="venue-loader-text">No more venues to load</p>}
                        {isError && <p className="venue-loader-text">{error?.message || "Failed to load venues"}</p>}
                    </div>
                </div>

                <div className="venue-page-design-bottom-footer-tags-section">
                    <div className="venue-page-detail-page-design-bottom-footer-tags">
                        <div className="venue-page-detail-page-design-bottom-footer-tags-text">
                            <h1>Instantly Book Tables at Top Lounges, Bars & Cafés Near You</h1>
                            <p>Looking for the perfect spot for a spontaneous night out or planned party? Instantly book tables at the best clubs, cafés, bars, and lounges near you with PartyWitty. Whether it's for a birthday, date night, or group hangout, explore top-rated venues across Delhi NCR. Check real-time availability, compare table offers, and reserve in just a few clicks. Enjoy exclusive discounts and hassle-free bookings at restaurants and clubs that match your vibe. Make every outing smooth and special — only with PartyWitty.</p>
                        </div>
                        <div className="venue-page-detail-page-design-bottom-footer-tags-text">
                            <h1>Book VIP Tables in Delhi NCR with Exclusive Deals!</h1>
                            <p>Skip the wait and grab the best seats in town! PartyWitty lets you book tables instantly at top clubs, lounges, and restaurants near you. Whether you're planning a dinner date, birthday celebration, or just a night out with friends, find verified venues across Delhi NCR. Access live availability, compare offers, and unlock up to 50% off on your booking. From rooftop views to dance floors and BYOB cafés — book your ideal spot in minutes with PartyWitty.</p>
                        </div>
                        <div className="venue-page-detail-page-design-bottom-footer-tags-text">
                            <h1>Reserve Tables with Custom Packages & Party Deals Instantly</h1>
                            <p>Why wait? Instantly reserve your table and unlock custom party deals with PartyWitty. From casual dinners to lively club nights, find the perfect place with the best offers near you. Choose from top restaurants, pubs, and lounges in Delhi, Noida, and Gurgaon — all offering personalized packages for birthdays, group parties, or corporate events. Check availability in real-time, pick your food and drink combos, and book your celebration spot in just a few taps.</p>
                        </div>
                    </div>
                </div>

                <FilterModal
                    isOpen={showFilter}
                    onClose={() => setShowFilter(false)}
                    setFiltersAndSort={setFiltersAndSort}
                    fetchDataWithFilters={mergedData}
                />
            </div>
        </>
    );
}
